import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { TextField, Switch, FormControlLabel, Tooltip, IconButton, Alert, Autocomplete } from "@mui/material";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";
import { useTheme } from "../../contexts/ThemeContext";
import { getData, patchData, postData } from "../../apis/api";
import { ENDPOINTS } from "../../apis/urls";

const API_BASE = "https://api-test.phloz.app";

const TRIGGER_TITLES = {
  INBOUND_CALL_COMPLETED: "Inbound call",
  WEBHOOK: "Webhook",
  MANUAL: "Manual",
  INSTAGRAM_COMMENT_RECEIVED: "Instagram comment",
  INSTAGRAM_DM_RECEIVED: "Instagram DM",
  LEADSQUARED_OWNER_CHANGED: "LeadSquared owner change",
};

const keywordsToText = (kw) => (Array.isArray(kw) ? kw.join(", ") : kw || "");
const textToKeywords = (text) =>
  String(text || "")
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);
const toIdList = (ids) => (Array.isArray(ids) ? ids.map(String) : textToKeywords(ids));

const postLabel = (post) => {
  const caption = (post.caption || "").replace(/\s+/g, " ").trim();
  const kind = post.mediaType ? `${post.mediaType} · ` : "";
  return `${kind}${caption ? caption.slice(0, 60) : post.permalink || post.mediaId}`;
};

export const TriggerModal = ({ open, handleClose, workflowId, onUpdated }) => {
  const { darkMode } = useTheme();
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Inbound call trigger form
  const [inboundEnabled, setInboundEnabled] = useState(false);
  const [inboundCooldown, setInboundCooldown] = useState(24);
  const [inboundMinDuration, setInboundMinDuration] = useState(0);

  // Webhook trigger form
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookToken, setWebhookToken] = useState("");

  // Manual trigger
  const [manualEnabled, setManualEnabled] = useState(true);

  // Instagram triggers
  const [igCommentEnabled, setIgCommentEnabled] = useState(false);
  const [igCommentKeywords, setIgCommentKeywords] = useState("");
  const [igCommentPosts, setIgCommentPosts] = useState([]);
  const [igDmEnabled, setIgDmEnabled] = useState(false);
  const [igDmKeywords, setIgDmKeywords] = useState("");
  const [igPosts, setIgPosts] = useState([]);

  // LeadSquared owner change trigger
  const [lsqEnabled, setLsqEnabled] = useState(false);
  const [lsqToken, setLsqToken] = useState("");

  // A webhook secret is returned once (on create or rotate)
  const [shownSecret, setShownSecret] = useState(null);

  const fetchTriggers = async () => {
    if (!workflowId) return;
    setLoading(true);
    try {
      const res = await getData(ENDPOINTS.WORKFLOW.TRIGGERS(workflowId));
      const list = Array.isArray(res) ? res : res?.Content || [];
      setTriggers(list);

      const inb = list.find((t) => t.triggerType === "INBOUND_CALL_COMPLETED");
      if (inb) {
        setInboundEnabled(inb.enabled);
        setInboundCooldown(inb.config?.cooldownHours ?? 24);
        setInboundMinDuration(inb.config?.minDurationSeconds ?? 0);
      } else {
        setInboundEnabled(false);
      }

      const wh = list.find((t) => t.triggerType === "WEBHOOK");
      if (wh) {
        setWebhookEnabled(wh.enabled);
        setWebhookToken(wh.webhookToken || "");
      } else {
        setWebhookEnabled(false);
        setWebhookToken("");
      }

      const man = list.find((t) => t.triggerType === "MANUAL");
      if (man) {
        setManualEnabled(man.enabled);
      } else {
        setManualEnabled(true);
      }

      const igc = list.find((t) => t.triggerType === "INSTAGRAM_COMMENT_RECEIVED");
      setIgCommentEnabled(!!igc?.enabled);
      setIgCommentKeywords(keywordsToText(igc?.config?.keywords));
      setIgCommentPosts(toIdList(igc?.config?.mediaIds || igc?.config?.media_ids));

      const igd = list.find((t) => t.triggerType === "INSTAGRAM_DM_RECEIVED");
      setIgDmEnabled(!!igd?.enabled);
      setIgDmKeywords(keywordsToText(igd?.config?.keywords));

      const lsq = list.find((t) => t.triggerType === "LEADSQUARED_OWNER_CHANGED");
      setLsqEnabled(!!lsq?.enabled);
      setLsqToken(lsq?.webhookToken || "");
    } catch (e) {
      console.error("Error fetching triggers:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setShownSecret(null);
      fetchTriggers();
      getData(ENDPOINTS.INSTAGRAM.POSTS(""))
        .then((res) => setIgPosts(res?.posts || []))
        .catch(() => setIgPosts([]));
    }
  }, [open, workflowId]);

  // One trigger per type: update the existing row rather than adding another.
  const saveTrigger = async (triggerType, enabled, config = {}) => {
    const existing = triggers.find((t) => t.triggerType === triggerType);
    try {
      if (existing) {
        await patchData(ENDPOINTS.WORKFLOW.TRIGGER(existing.id), {
          enabled,
          config: { ...(existing.config || {}), ...config },
        });
      } else {
        const res = await postData(ENDPOINTS.WORKFLOW.TRIGGERS(workflowId), {
          triggerType,
          enabled,
          config,
        });
        const created = res?.Content || res;
        if (created?.id) setTriggers((prev) => [created, ...prev]);
        if (created?.webhookSecretPlaintext) {
          setShownSecret({ header: created.webhookSecretHeader, value: created.webhookSecretPlaintext });
        }
      }
      toast.success(`${TRIGGER_TITLES[triggerType] || triggerType} trigger saved`);
      fetchTriggers();
      if (onUpdated) onUpdated();
    } catch (err) {
      const detail = Array.isArray(err?.errors) ? err.errors.join(", ") : err?.message;
      toast.error(detail ? `Failed to save trigger: ${detail}` : "Failed to save trigger");
    }
  };

  const rotateSecret = async (triggerType, header) => {
    const trigger = triggers.find((t) => t.triggerType === triggerType);
    if (!trigger) return;
    try {
      const res = await postData(ENDPOINTS.WORKFLOW.ROTATE_SECRET(trigger.id), {});
      setShownSecret({ header, value: res?.webhookSecretPlaintext });
      toast.success("Secret rotated. Update it wherever this webhook is configured.");
    } catch (e) {
      toast.error("Failed to rotate secret");
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard!");
  };

  const saveIgComment = (enabled) =>
    saveTrigger("INSTAGRAM_COMMENT_RECEIVED", enabled, {
      keywords: textToKeywords(igCommentKeywords),
      mediaIds: igCommentPosts,
      media_ids: undefined,
    });

  const saveIgDm = (enabled) =>
    saveTrigger("INSTAGRAM_DM_RECEIVED", enabled, { keywords: textToKeywords(igDmKeywords) });

  // Switching on a new Instagram trigger only opens its form: saving it straight
  // away would match every comment/DM until posts and keywords are filled in.
  const toggleInstagram = (triggerType, enabled, setEnabled, save) => {
    setEnabled(enabled);
    if (triggers.some((t) => t.triggerType === triggerType)) save(enabled);
  };

  const webhookUrl = `${API_BASE}/workflow/webhooks/enroll/${webhookToken || "..."}`;
  const lsqUrl = `${API_BASE}/workflow/webhooks/leadsquared/${lsqToken || "..."}`;
  const postsById = new Map(igPosts.map((p) => [String(p.mediaId), p]));

  const cardClass = `p-4 rounded-xl border ${darkMode ? "bg-zinc-900/60 border-zinc-700" : "bg-slate-50 border-slate-200"}`;
  const bodyClass = "mt-3 pt-3 border-t border-slate-200 dark:border-zinc-700 space-y-3";
  const renderHeader = (title, description, checked, onToggle) => (
    <div className="flex items-center justify-between">
      <div>
        <div className={`text-sm font-semibold ${darkMode ? "text-white" : "text-slate-800"}`}>{title}</div>
        <div className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>{description}</div>
      </div>
      <FormControlLabel
        control={<Switch checked={checked} onChange={(e) => onToggle(e.target.checked)} color="primary" />}
        label=""
      />
    </div>
  );
  const renderUrl = (url, onRotate) => (
    <div className="flex items-center gap-1.5">
      <input
        readOnly
        value={url}
        className={`text-xs font-mono p-2 rounded flex-1 border ${
          darkMode ? "bg-zinc-800 border-zinc-700 text-zinc-200" : "bg-white border-slate-300 text-slate-800"
        }`}
      />
      <Tooltip title="Copy URL">
        <IconButton size="small" onClick={() => copyText(url)} color="primary">
          {copied ? <CheckCircleOutlineIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Rotate Secret">
        <IconButton size="small" onClick={onRotate}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
            <FlashOnIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              Workflow Enrollment Triggers
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Configure rules and events that automatically enroll contacts into this Journey.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          {/* Inbound Call Trigger */}
          <div className={`p-4 rounded-xl border ${darkMode ? "bg-zinc-900/60 border-zinc-700" : "bg-slate-50 border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-sm font-semibold ${darkMode ? "text-white" : "text-slate-800"}`}>
                  Inbound Call Completed
                </div>
                <div className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  Enrolls caller automatically when an inbound voice call finishes.
                </div>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={inboundEnabled}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setInboundEnabled(val);
                      saveTrigger("INBOUND_CALL_COMPLETED", val, {
                        cooldownHours: Number(inboundCooldown),
                        minDurationSeconds: Number(inboundMinDuration),
                      });
                    }}
                    color="primary"
                  />
                }
                label=""
              />
            </div>

            {inboundEnabled && (
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-200 dark:border-zinc-700">
                <TextField
                  size="small"
                  type="number"
                  label="Cooldown (Hours)"
                  value={inboundCooldown}
                  onChange={(e) => setInboundCooldown(Number(e.target.value))}
                  onBlur={() =>
                    saveTrigger("INBOUND_CALL_COMPLETED", true, {
                      cooldownHours: Number(inboundCooldown),
                      minDurationSeconds: Number(inboundMinDuration),
                    })
                  }
                  helperText="Prevents re-enrolling same contact within X hours"
                />
                <TextField
                  size="small"
                  type="number"
                  label="Min Call Duration (Sec)"
                  value={inboundMinDuration}
                  onChange={(e) => setInboundMinDuration(Number(e.target.value))}
                  onBlur={() =>
                    saveTrigger("INBOUND_CALL_COMPLETED", true, {
                      cooldownHours: Number(inboundCooldown),
                      minDurationSeconds: Number(inboundMinDuration),
                    })
                  }
                  helperText="Only trigger if call exceeded N seconds"
                />
              </div>
            )}
          </div>

          {/* Webhook API Trigger */}
          <div className={`p-4 rounded-xl border ${darkMode ? "bg-zinc-900/60 border-zinc-700" : "bg-slate-50 border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-sm font-semibold ${darkMode ? "text-white" : "text-slate-800"}`}>
                  Inbound Webhook API
                </div>
                <div className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  POST contact payloads from your CRM or website directly to this endpoint.
                </div>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={webhookEnabled}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setWebhookEnabled(val);
                      saveTrigger("WEBHOOK", val);
                    }}
                    color="primary"
                  />
                }
                label=""
              />
            </div>

            {webhookEnabled && (
              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-zinc-700 space-y-2">
                <label className={`text-xs font-semibold block ${darkMode ? "text-zinc-300" : "text-slate-700"}`}>
                  Webhook Endpoint URL:
                </label>
                {renderUrl(webhookUrl, () => rotateSecret("WEBHOOK"))}
                <div className={`text-[11px] ${darkMode ? "text-zinc-500" : "text-slate-400"}`}>
                  Payload format: &#123; "phone": "+1234567890", "Name": "Jane", "context": &#123; ... &#125; &#125;
                </div>
              </div>
            )}
          </div>

          {shownSecret?.value && (
            <Alert severity="warning" onClose={() => setShownSecret(null)}>
              <div className="text-xs">
                Copy this secret now, it is shown only once
                {shownSecret.header ? ` (send it in the ${shownSecret.header} header)` : ""}:
              </div>
              <div className="flex items-center gap-1 mt-1">
                <code className="text-xs break-all">{shownSecret.value}</code>
                <IconButton size="small" onClick={() => copyText(shownSecret.value)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </div>
            </Alert>
          )}

          {/* Instagram Comment Trigger */}
          <div className={cardClass}>
            {renderHeader(
              "Instagram Comment",
              "Enrolls a user who comments on your Instagram posts or reels.",
              igCommentEnabled,
              (val) => toggleInstagram("INSTAGRAM_COMMENT_RECEIVED", val, setIgCommentEnabled, saveIgComment)
            )}
            {igCommentEnabled && (
              <div className={bodyClass}>
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={igPosts.map((p) => String(p.mediaId))}
                  value={igCommentPosts}
                  onChange={(e, value) => setIgCommentPosts(value.map((v) => String(v).trim()).filter(Boolean))}
                  getOptionLabel={(mediaId) => (postsById.has(mediaId) ? postLabel(postsById.get(mediaId)) : mediaId)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Posts / Reels"
                      placeholder="Any post"
                      helperText="Leave empty for all posts. A post-specific trigger takes precedence over all-post triggers."
                    />
                  )}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Keywords"
                  value={igCommentKeywords}
                  onChange={(e) => setIgCommentKeywords(e.target.value)}
                  placeholder="price, details"
                  helperText="Comma separated. Leave empty to match every comment."
                />
                <div className="flex justify-end">
                  <Button size="small" variant="contained" onClick={() => saveIgComment(true)}>
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Instagram DM Trigger */}
          <div className={cardClass}>
            {renderHeader(
              "Instagram DM",
              "Enrolls a user who sends your Instagram account a direct message.",
              igDmEnabled,
              (val) => toggleInstagram("INSTAGRAM_DM_RECEIVED", val, setIgDmEnabled, saveIgDm)
            )}
            {igDmEnabled && (
              <div className={bodyClass}>
                <TextField
                  fullWidth
                  size="small"
                  label="Keywords"
                  value={igDmKeywords}
                  onChange={(e) => setIgDmKeywords(e.target.value)}
                  placeholder="turkey"
                  helperText="Comma separated. Leave empty to match every DM. A keyword trigger takes precedence over catch-all DM triggers."
                />
                <div className="flex justify-end">
                  <Button size="small" variant="contained" onClick={() => saveIgDm(true)}>
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* LeadSquared Owner Change Trigger */}
          <div className={cardClass}>
            {renderHeader(
              "LeadSquared Lead Owner Changed",
              "Enrolls a lead when its owner changes in LeadSquared.",
              lsqEnabled,
              (val) => {
                setLsqEnabled(val);
                saveTrigger("LEADSQUARED_OWNER_CHANGED", val, { onConflict: "replace" });
              }
            )}
            {lsqEnabled && lsqToken && (
              <div className={bodyClass}>
                {renderUrl(lsqUrl, () => rotateSecret("LEADSQUARED_OWNER_CHANGED", "X-Phloz-Webhook-Secret"))}
                <div className={`text-[11px] ${darkMode ? "text-zinc-500" : "text-slate-400"}`}>
                  In LeadSquared: Settings &gt; API and Webhooks &gt; Webhooks, event "Lead Ownership Change",
                  content type application/json, secret in the X-Phloz-Webhook-Secret header.
                </div>
              </div>
            )}
          </div>

          {/* Manual Trigger */}
          <div className={`p-4 rounded-xl border ${darkMode ? "bg-zinc-900/60 border-zinc-700" : "bg-slate-50 border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-sm font-semibold ${darkMode ? "text-white" : "text-slate-800"}`}>
                  Manual & Bulk Enrollment
                </div>
                <div className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  Allows operators to manually enroll contacts or segment lists into this Journey.
                </div>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={manualEnabled}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setManualEnabled(val);
                      saveTrigger("MANUAL", val);
                    }}
                    color="primary"
                  />
                }
                label=""
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="contained" onClick={handleClose} style={{ backgroundColor: "#2563eb" }}>
            Done
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

